// src/components/tutor/mobile/MobileTutorSettings.js
import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";
import { requestNotificationPermission } from "../../admin/notification/NotificationLogic";
import "./MobileTutorSettings.css";

const DEFAULT_PREFS = {
  notifyAnnouncements:     true,
  notifyCancellations:     true,
  notifySessionCreated:    true,
  notifyTimeChange:        true,
  notifyDateChange:        true,
  notifyTutorReassignment: true,
  notifyNoShow:            true,
  notifySubjectChange:     true,
};

function ToggleBtn({ value, onToggle, ariaLabel }) {
  return (
    <button
      className="mts-toggle"
      onClick={onToggle}
      style={{
        background:     value ? "#111827" : "#E5E7EB",
        justifyContent: value ? "flex-end" : "flex-start",
      }}
      aria-label={ariaLabel}
    >
      <div className="mts-toggle-handle" />
    </button>
  );
}

function PrefRow({ label, sub, prefKey, prefs, onToggle, isLast }) {
  return (
    <div className={`mts-row${isLast ? " mts-row--last" : ""}`}>
      <div className="mts-row-info">
        <span className="mts-row-label">{label}</span>
        <span className="mts-row-sub">{sub}</span>
      </div>
      <ToggleBtn
        value={prefs[prefKey]}
        onToggle={() => onToggle(prefKey)}
        ariaLabel={`Toggle ${label}`}
      />
    </div>
  );
}

export default function MobileTutorSettings() {
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [statusLabel,   setStatusLabel]   = useState("Checking…");
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    checkPermissionAndPrefs();
  }, []);

  const checkPermissionAndPrefs = async () => {
    if (Notification.permission === "granted") {
      setMasterEnabled(true);
      setStatusLabel("Active");
    } else if (Notification.permission === "denied") {
      setMasterEnabled(false);
      setStatusLabel("Blocked in browser settings");
    } else {
      setMasterEnabled(false);
      setStatusLabel("Disabled");
    }

    if (auth.currentUser) {
      try {
        const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (snap.exists()) {
          const d = snap.data();
          setPrefs({
            notifyAnnouncements:     d.notifyAnnouncements     !== false,
            notifyCancellations:     d.notifyCancellations     !== false,
            notifySessionCreated:    d.notifySessionCreated    !== false,
            notifyTimeChange:        d.notifyTimeChange        !== false,
            notifyDateChange:        d.notifyDateChange        !== false,
            notifyTutorReassignment: d.notifyTutorReassignment !== false,
            notifyNoShow:            d.notifyNoShow            !== false,
            notifySubjectChange:     d.notifySubjectChange     !== false,
          });
        }
      } catch (err) {
        console.error("Error loading prefs:", err);
      }
    }
  };

  const handleMasterToggle = async () => {
    if (masterEnabled) {
      alert("To disable notifications, reset permissions in your browser or device settings.");
      return;
    }
    if (!auth.currentUser) return;
    const granted = await requestNotificationPermission(auth.currentUser.uid);
    if (granted) {
      setMasterEnabled(true);
      setStatusLabel("Active");
      const allEnabled = Object.fromEntries(Object.keys(DEFAULT_PREFS).map(k => [k, true]));
      setPrefs(allEnabled);
      await savePreferences(allEnabled);
      alert("Notifications enabled!");
    } else {
      if (Notification.permission === "denied") {
        alert("Notifications are blocked. Please update your browser settings.");
      }
      setMasterEnabled(false);
    }
  };

  const handleSubToggle = async (key) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    await savePreferences(newPrefs);
  };

  const savePreferences = async (p) => {
    if (!auth.currentUser) return;
    await setDoc(doc(db, "users", auth.currentUser.uid), p, { merge: true });
  };

  return (
    <div className="mts-page">

      {/* ── Master enable ── */}
      <p className="mts-section-label">Push Notifications</p>
      <div className="mts-card">
        <div className="mts-row mts-row--last">
          <div className="mts-row-info">
            <span className="mts-row-label">Enable Notifications</span>
            <span className="mts-row-sub">Receive alerts on this device</span>
            <span
              className="mts-row-status"
              style={{ color: masterEnabled ? "#16A34A" : "#6B7280" }}
            >
              {statusLabel}
            </span>
          </div>
          <ToggleBtn
            value={masterEnabled}
            onToggle={handleMasterToggle}
            ariaLabel="Toggle push notifications"
          />
        </div>
      </div>

      {masterEnabled && (
        <>
          {/* ── Announcements ── */}
          <p className="mts-section-label" style={{ marginTop: 20 }}>Announcements</p>
          <div className="mts-card">
            <PrefRow
              label="Announcements"
              sub="New updates and posts from admin"
              prefKey="notifyAnnouncements"
              prefs={prefs}
              onToggle={handleSubToggle}
              isLast
            />
          </div>

          {/* ── Session Changes ── */}
          <p className="mts-section-label" style={{ marginTop: 20 }}>Session Changes</p>
          <div className="mts-card">
            <PrefRow
              label="Confirmed Sessions"
              sub="Alerts when a session assigned to you is confirmed"
              prefKey="notifySessionCreated"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="Cancelled Sessions"
              sub="Alerts when a confirmed session is cancelled"
              prefKey="notifyCancellations"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="Time Changed"
              sub="Alerts when a session's start or end time changes"
              prefKey="notifyTimeChange"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="Day / Date Changed"
              sub="Alerts when a session is moved to a different day"
              prefKey="notifyDateChange"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="Tutor Reassigned"
              sub="Alerts when a session's assigned tutor changes"
              prefKey="notifyTutorReassignment"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="No Show"
              sub="Alerts when a confirmed session is marked as no show"
              prefKey="notifyNoShow"
              prefs={prefs}
              onToggle={handleSubToggle}
            />
            <PrefRow
              label="Subject Changed"
              sub="Alerts when a session's subject is updated"
              prefKey="notifySubjectChange"
              prefs={prefs}
              onToggle={handleSubToggle}
              isLast
            />
          </div>
        </>
      )}

    </div>
  );
}
